import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
  input,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  QaiButtonComponent,
  QaiDialogComponent,
  QaiScoreWeightageComponent,
  QaiItemGridComponent,
} from '@qai/ui';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { IScoreWeight } from 'src/app/interfaces/self-serve/isimulation-score';

@Component({
  selector: 'edit-weighting-modal',
  standalone: true,
  templateUrl: './edit-weighting-modal.component.html',
  styleUrls: ['./edit-weighting-modal.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    QaiButtonComponent,
    QaiDialogComponent,
    QaiScoreWeightageComponent,
    QaiItemGridComponent,
    TranslocoModule,
  ],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class EditWeightingModalComponent {
  @ViewChild('dialog') dialogRef: QaiDialogComponent | undefined;
  @Input('open') open: boolean = false;
  @Input() weights: IScoreWeight[] = [];
  isSelfAuthoring = input(false);
  @Output() confirm = new EventEmitter<IScoreWeight[]>();
  @Output() closeModal = new EventEmitter<void>();

  localWeights: IScoreWeight[] = [];
  errorMessage: string = '';
  fieldErrors: { [key: string]: boolean } = {};

  constructor(private translocoService: TranslocoService) {}

  ngOnChanges(changes: SimpleChanges) {
    if (this.weights.length > 0) {
      this.localWeights = this.weights.map((w) => ({ ...w }));

      if (this.isSelfAuthoring()) {
        const methodology = this.localWeights.find(
          (w) => w.section === 'Methodology'
        );
        if (methodology) {
          methodology.disabled = false;
        }
      }
    }

    if (changes['open']?.currentValue) {
      this.errorMessage = '';
      this.showModal();
    }
  }

  areWeightsValid(): boolean {
    this.fieldErrors = {};
    this.errorMessage = '';

    const totalWeight = this.localWeights.reduce((sum, weight) => {
      return weight.value !== 0 ? sum + Number(weight.value) : sum;
    }, 0);

    const allDisabled = this.localWeights.every(
      (weight) => weight.disabled === true
    );
    if (allDisabled) {
      this.errorMessage = this.translocoService.translate('simulationBuilder.EDIT_WEIGHTING_ERROR_ALL_DISABLED');
      return false;
    }

    let hasErrors = false;

    this.localWeights.forEach((weight, index) => {
      const fieldKey = `weight-${index}`;
      const value = Number(weight.value);

      if (weight.value === 0) {
        this.fieldErrors[fieldKey] = false;
        return;
      }

      if (value < 0 || value > 100) {
        this.fieldErrors[fieldKey] = true;
        this.errorMessage = this.translocoService.translate('simulationBuilder.EDIT_WEIGHTING_ERROR_WEIGHT_RANGE');
        hasErrors = true;
      } else if (weight.section === 'Skills' && value > 20) {
        this.fieldErrors[fieldKey] = true;
        this.errorMessage = this.translocoService.translate('simulationBuilder.EDIT_WEIGHTING_ERROR_SKILLS_MAX');
        hasErrors = true;
      } else {
        this.fieldErrors[fieldKey] = false;
      }
    });

    if (!hasErrors && Math.round(totalWeight) !== 100) {
      this.errorMessage = this.translocoService.translate('simulationBuilder.EDIT_WEIGHTING_ERROR_TOTAL');
      hasErrors = true;
    }

    return !hasErrors;
  }

  handleConfirm() {
    if (!this.areWeightsValid()) {
      return;
    }

    const updatedWeights = this.localWeights.map((weight) => ({
      ...weight,
    }));
    this.confirm.emit(updatedWeights);
    this.hideModal();
  }

  handleClose() {
    this.closeModal.emit();
  }

  hideModal() {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  showModal() {
    if (this.dialogRef) {
      this.dialogRef.showModal();
    }
  }

  onDisableChange(checked: boolean, i: number) {
    if (this.isSelfAuthoring() && this.localWeights[i]?.section === 'Methodology') {
      return;
    }

    if (checked) {
      // Enable the section
      this.localWeights[i].value = this.isSelfAuthoring()
        ? (this.weights?.[i]?.value ?? 0)
        : (this.weights?.[i]?.value || 1);
      this.localWeights[i].disabled = false;
    } else {
      // Disable the section
      this.localWeights[i].value = 0;
      this.localWeights[i].disabled = true;
    }
    this.areWeightsValid();
  }

  onWeightInputChange(value: number, index: number) {
    this.localWeights[index].value = Number(value);
    this.areWeightsValid();
  }

  hasErrors(): boolean {
    return (
      Object.values(this.fieldErrors).some((val) => val === true) ||
      this.errorMessage !== ''
    );
  }
}
